import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { UrlReferenceService } from './url-reference.service';

export interface DashboardMetrics {
  totalEmployees: number;
  totalCompanies: number;
  totalActivePolicies: number;
  activeQuotes: number;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardMetricsService {

  constructor(
    private http: HttpClient,
    private urlRef: UrlReferenceService
  ) {}

  /**
   * Get total employees for a user
   */
  getTotalEmployeesForUser(userId: string, companyId: number): Observable<number> {
    const params = new HttpParams().set('companyId', companyId);
    const url = this.urlRef.getDashboardMetricsTotalEmployeesUrl(userId);
    return this.http.get<number>(url, { params });
  }

  /**
   * Get total companies for a user
   */
  getTotalCompaniesForUser(userId: string): Observable<number> {
    const url = this.urlRef.getDashboardMetricsTotalCompaniesUrl(userId);
    return this.http.get<number>(url);
  }

  /**
   * Get total active policies for a user
   */
  getTotalActivePoliciesForUser(userId: string, companyId: number): Observable<number> {
    const params = new HttpParams().set('companyId', companyId);
    const url = this.urlRef.getDashboardMetricsTotalActivePoliciesUrl(userId);
    return this.http.get<number>(url, { params });
  }

  /**
   * Get active quotes count for a user
   */
  getActiveQuotesForUser(userId: string, companyId: number): Observable<number> {
    const params = new HttpParams().set('companyId', companyId);
    const url = this.urlRef.getDashboardMetricsActiveQuotesUrl(userId);
    return this.http.get<number>(url, { params });
  }

  /**
   * Get all dashboard metrics for a user in one call
   */
  getAllMetricsForUser(userId: string, companyId: number): Observable<DashboardMetrics> {
    // Since the backend doesn't have a single endpoint for all metrics,
    // we'll make parallel calls and combine the results
    return new Observable<DashboardMetrics>(observer => {
      const metrics: Partial<DashboardMetrics> = {};
      let completedRequests = 0;
      const totalRequests = 4; // Updated to include active quotes

      const checkComplete = () => {
        completedRequests++;
        if (completedRequests === totalRequests) {
          observer.next(metrics as DashboardMetrics);
          observer.complete();
        }
      };

      // Get total employees
      this.getTotalEmployeesForUser(userId, companyId).subscribe({
        next: (count) => {
          metrics.totalEmployees = count;
          checkComplete();
        },
        error: (error) => {
          console.error('Error fetching total employees:', error);
          metrics.totalEmployees = 0;
          checkComplete();
        }
      });

      // Get total companies
      this.getTotalCompaniesForUser(userId).subscribe({
        next: (count) => {
          metrics.totalCompanies = count;
          checkComplete();
        },
        error: (error) => {
          console.error('Error fetching total companies:', error);
          metrics.totalCompanies = 0;
          checkComplete();
        }
      });

      // Get total active policies
      this.getTotalActivePoliciesForUser(userId, companyId).subscribe({
        next: (count) => {
          metrics.totalActivePolicies = count;
          checkComplete();
        },
        error: (error) => {
          console.error('Error fetching total active policies:', error);
          metrics.totalActivePolicies = 0;
          checkComplete();
        }
      });

      // Get active quotes
      this.getActiveQuotesForUser(userId, companyId).subscribe({
        next: (count) => {
          metrics.activeQuotes = count;
          checkComplete();
        },
        error: (error) => {
          console.error('Error fetching active quotes:', error);
          metrics.activeQuotes = 0;
          checkComplete();
        }
      });
    });
  }
}
